/**
 * Mantenimiento → Tareas (`/mantenimiento/tareas`).
 *
 * Puerto de `Maintenance_tasks::index` + `views/maintenance/tasks/index.php`:
 * el listado con sus filtros (categoría, activo, estado, prioridad y aprobación)
 * y el resumen por estado, más el alta y el detalle con todo el ciclo (iniciar,
 * cerrar con lecturas, aprobar o rechazar y adjuntar evidencias).
 */

import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Eye,
  Filter,
  Plus,
  Search,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useAuthStore } from '@/features/auth/auth.store';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { TareaDialogo } from '../components/tarea-dialogo';
import { TareaDetalle } from '../components/tarea-detalle';
import { useCrearTarea, useTareas, useTareasCatalogos } from '../tareas.api';
import type {
  EstadoTarea,
  FiltroAprobacion,
  PrioridadTarea,
  TareaPayload,
} from '../tareas.types';

/** Paginación amplia: los filtros finos se aplican en el cliente. */
const POR_PAGINA = 200;

const CONTROL =
  'h-12 rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Variante del badge según la prioridad (como el panel anterior). */
function variantePrioridad(prioridad: PrioridadTarea): 'danger' | 'warning' | 'neutral' {
  if (prioridad === 'CRÍTICA') return 'danger';
  if (prioridad === 'ALTA') return 'warning';

  return 'neutral';
}

/** Variante del badge según el estado de ejecución. */
function varianteEstado(estado: EstadoTarea): 'danger' | 'brand' | 'success' {
  if (estado === 'PENDIENTE') return 'danger';
  if (estado === 'EN_PROCESO') return 'brand';

  return 'success';
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function TareasPage() {
  useDocumentTitle('Mantenimiento · Tareas');

  const esAdmin = useAuthStore((estado) => estado.user?.isAdmin ?? false);

  const [busqueda, setBusqueda] = useState('');
  const [categoriaId, setCategoriaId] = useState(0);
  const [activoId, setActivoId] = useState(0);
  const [estado, setEstado] = useState<EstadoTarea | ''>('');
  const [prioridad, setPrioridad] = useState<PrioridadTarea | ''>('');
  const [aprobacion, setAprobacion] = useState<FiltroAprobacion>('todas');

  const [dialogo, setDialogo] = useState(false);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const tareas = useTareas({ perPage: POR_PAGINA });
  const catalogos = useTareasCatalogos();
  const crear = useCrearTarea();

  const sinPermiso = tareas.error instanceof ApiError && tareas.error.status === 403;
  const lista = tareas.data?.tareas ?? [];
  const total = tareas.data?.total ?? 0;
  const resumen = tareas.data?.resumen;

  const categorias = catalogos.data?.categorias ?? [];
  const activos = categorias.flatMap((categoria) =>
    categoria.activos.map((activo) => ({ ...activo, categoria: categoria.nombre })),
  );

  const termino = busqueda.trim().toLowerCase();
  const filtradas = lista.filter((tarea) => {
    if (estado && tarea.estado !== estado) return false;
    if (prioridad && tarea.prioridad !== prioridad) return false;
    if (categoriaId && tarea.categoria_id !== categoriaId) return false;
    if (activoId && tarea.activo_id !== activoId) return false;

    if (aprobacion === 'aprobadas' && !tarea.aprobada) return false;
    if (aprobacion === 'por_aprobar' && !(tarea.estado === 'REALIZADO' && !tarea.aprobada)) {
      return false;
    }
    if (aprobacion === 'rechazadas' && !(tarea.comentario_aprobacion && !tarea.aprobada)) {
      return false;
    }

    if (!termino) return true;

    return (
      tarea.tipo.toLowerCase().includes(termino) ||
      tarea.descripcion.toLowerCase().includes(termino) ||
      tarea.activo.toLowerCase().includes(termino) ||
      tarea.sub_activo.toLowerCase().includes(termino)
    );
  });

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/mantenimiento"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Mantenimiento
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <ClipboardList className="size-6 text-brand-500" aria-hidden="true" />
          Tareas de mantenimiento
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          El trabajo pendiente sobre cada sub-activo: se ejecuta en tres pasos y sólo un
          administrador lo cierra al aprobarlo.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {resumen ? (
          <Badge variant={resumen.sin_aprobar > 0 ? 'warning' : 'outline'}>
            {total} tareas · {resumen.sin_aprobar} por aprobar
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso || categorias.length === 0}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo(true);
          }}
        >
          Nueva tarea
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>maintenance</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      {resumen ? (
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-card border border-rose-100 bg-rose-50/60 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-rose-500">Pendientes</p>
            <p className="text-2xl font-bold text-rose-700">{resumen.pendientes}</p>
          </div>
          <div className="rounded-card border border-brand-100 bg-brand-50/60 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-500">
              En proceso
            </p>
            <p className="text-2xl font-bold text-brand-700">{resumen.en_proceso}</p>
          </div>
          <div className="rounded-card border border-emerald-100 bg-emerald-50/60 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">
              Realizadas
            </p>
            <p className="text-2xl font-bold text-emerald-700">{resumen.realizadas}</p>
          </div>
          <div className="rounded-card border border-amber-100 bg-amber-50/60 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">
              Por aprobar
            </p>
            <p className="text-2xl font-bold text-amber-700">{resumen.sin_aprobar}</p>
          </div>
        </div>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              containerClassName="min-w-56 flex-1"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar por tipo, descripción, activo o sub-activo…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />

            <Select
              aria-label="Filtrar por categoría"
              className={cn(CONTROL, 'w-auto min-w-36')}
              value={String(categoriaId)}
              onChange={(evento) => {
                setCategoriaId(Number(evento.target.value));
                setActivoId(0);
              }}
            >
              <option value="0">Todas las categorías</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por activo"
              className={cn(CONTROL, 'w-auto min-w-40')}
              value={String(activoId)}
              onChange={(evento) => setActivoId(Number(evento.target.value))}
            >
              <option value="0">Todos los activos</option>
              {activos
                .filter((activo) => !categoriaId || activo.categoria === categorias.find((c) => c.id === categoriaId)?.nombre)
                .map((activo) => (
                  <option key={activo.id} value={activo.id}>
                    {activo.nombre} · {activo.codigo}
                  </option>
                ))}
            </Select>

            <Select
              aria-label="Filtrar por estado"
              className={cn(CONTROL, 'w-auto min-w-32')}
              value={estado}
              onChange={(evento) => setEstado(evento.target.value as EstadoTarea | '')}
            >
              <option value="">Todos los estados</option>
              {(catalogos.data?.estados ?? []).map((item) => (
                <option key={item.valor} value={item.valor}>
                  {item.etiqueta}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por prioridad"
              className={cn(CONTROL, 'w-auto min-w-32')}
              value={prioridad}
              onChange={(evento) => setPrioridad(evento.target.value as PrioridadTarea | '')}
            >
              <option value="">Todas las prioridades</option>
              {(catalogos.data?.prioridades ?? []).map((valor) => (
                <option key={valor} value={valor}>
                  {valor}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex items-center gap-1 rounded-control border border-ink-200 bg-white p-1">
            <Filter className="mx-1 size-3.5 text-ink-400" aria-hidden="true" />
            {(['todas', 'por_aprobar', 'aprobadas', 'rechazadas'] as const).map((opcion) => (
              <button
                key={opcion}
                type="button"
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  aprobacion === opcion
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-ink-500 hover:bg-ink-50 hover:text-ink-700',
                )}
                onClick={() => setAprobacion(opcion)}
              >
                {opcion === 'por_aprobar'
                  ? 'por aprobar'
                  : opcion === 'rechazadas'
                    ? 'rechazadas'
                    : opcion}
              </button>
            ))}
          </div>

          {tareas.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : tareas.error ? (
            <Alert variant="error" title="No se pudieron leer las tareas">
              {mensajeDe(tareas.error, 'La API no respondió.')}
            </Alert>
          ) : filtradas.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              {termino || estado || prioridad || categoriaId || activoId || aprobacion !== 'todas'
                ? 'Ninguna tarea coincide con el filtro.'
                : 'Todavía no hay tareas registradas.'}
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">#</th>
                    <th className="py-2">Tarea</th>
                    <th className="py-2">Sub-activo</th>
                    <th className="py-2">Prioridad</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2">Aprobación</th>
                    <th className="py-2">Ejecución</th>
                    <th className="py-2">Evidencias</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((tarea) => (
                    <tr key={tarea.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3 font-mono text-xs text-ink-400">{tarea.id}</td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">{tarea.tipo}</span>
                        <span className="block text-[11px] text-ink-400">
                          {tarea.activo} · {tarea.categoria}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="block text-ink-700">{tarea.sub_activo}</span>
                        <span className="block font-mono text-[11px] text-ink-400">
                          {tarea.sub_activo_codigo}
                        </span>
                      </td>
                      <td className="py-3">
                        <Badge variant={variantePrioridad(tarea.prioridad)}>{tarea.prioridad}</Badge>
                      </td>
                      <td className="py-3">
                        <Badge variant={varianteEstado(tarea.estado)}>{tarea.estado_etiqueta}</Badge>
                      </td>
                      <td className="py-3">
                        {tarea.aprobada ? (
                          <Badge variant="success">
                            <CheckCircle2 className="size-3" aria-hidden="true" />
                            Aprobada
                          </Badge>
                        ) : (
                          <Badge variant="neutral">Sin aprobar</Badge>
                        )}
                      </td>
                      <td className="py-3 text-ink-600">
                        {tarea.ejecucion ? (
                          tarea.ejecucion
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                        {tarea.programada ? (
                          <span className="block text-[11px] text-ink-400">
                            Prog. {tarea.programada}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3">
                        {tarea.evidencias > 0 ? (
                          <Badge variant="brand">{tarea.evidencias}</Badge>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Ver detalle"
                            aria-label={`Ver la tarea ${tarea.tipo} #${tarea.id}`}
                            onClick={() => setDetalleId(tarea.id)}
                          >
                            <Eye className="size-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > lista.length ? (
            <p className="text-xs text-ink-400">
              Mostrando {lista.length} de {total} tareas. Usa los filtros para acotar.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {dialogo && catalogos.data ? (
        <TareaDialogo
          catalogos={catalogos.data}
          guardando={crear.isPending}
          error={errorDialogo}
          onGuardar={(payload: TareaPayload) => {
            setErrorDialogo(null);
            crear.mutate(payload, {
              onSuccess: (respuesta) => {
                setAviso(respuesta.mensaje);
                setDialogo(false);
              },
              onError: (error) => setErrorDialogo(mensajeDe(error, 'La API no respondió.')),
            });
          }}
          onCerrar={() => setDialogo(false)}
        />
      ) : null}

      {detalleId !== null ? (
        <TareaDetalle
          tareaId={detalleId}
          esAdmin={esAdmin}
          onAviso={(mensaje) => setAviso(mensaje)}
          onCerrar={() => setDetalleId(null)}
        />
      ) : null}

      {lista.length === 0 && !tareas.isLoading && !tareas.error ? (
        <p className="flex items-center gap-2 text-xs text-ink-400">
          <Wrench className="size-3" aria-hidden="true" />
          Las tareas se crean sobre sub-activos, así que primero necesitas categorías, activos y
          componentes.
        </p>
      ) : null}
    </div>
  );
}
